<script setup lang="ts">
import { toSchemaDate, useThemeLogo } from '#imports'
import { useHeroConfig } from '../../composables/hero/useHeroConfig'

export interface NimbusBlogPostProps {
    title: string
    subtitle: string
    label: string
    mediaUrl: string
    relativeDate: string
    publishedAt: string
}

const props = defineProps<NimbusBlogPostProps>()

const { imagePreset } = useHeroConfig()
const brandIcon = useThemeLogo('icon')
</script>

<template>
    <div class="blog-post">
        <div
            class="blog-post__thumbnail"
            :class="{ 'blog-post__thumbnail--fallback': !props.mediaUrl }"
        >
            <NuxtImg
                v-if="props.mediaUrl"
                provider="cloudflare"
                :preset="imagePreset"
                :src="props.mediaUrl"
                width="80"
                height="80"
                loading="lazy"
                decoding="async"
            />

            <NuxtImg
                v-else-if="brandIcon"
                class="blog-post__fallback-logo"
                provider="cloudflare"
                :preset="imagePreset"
                :src="brandIcon.src"
                :quality="brandIcon.quality"
                alt=""
                width="48"
                height="48"
                loading="lazy"
                decoding="async"
            />
        </div>

        <div class="blog-post__content">
            <div class="blog-post__date">
                <div class="blog-post__label">
                    {{ props.label }}
                </div>

                <time class="blog-post__created" :datetime="toSchemaDate(props.publishedAt)">
                    {{ props.relativeDate }}
                </time>
            </div>

            <div class="blog-post__heading">
                <div class="blog-post__title">
                    {{ props.title }}
                </div>
                <div class="blog-post__subtitle">
                    {{ props.subtitle }}
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.blog-post {
    display: grid;
    grid-template-columns: 80px 1fr auto;
    gap: 20px;

    border-radius: $border-radius;
    padding: 12px;
    background-color: var(--panel-bg, $panel-color);

    cursor: pointer;

    &:hover img {
        scale: 1.05;
    }

    &__thumbnail {
        height: 100%;
        width: 100%;
        border-radius: $border-radius;
        overflow: hidden;

        > img {
            height: 100%;
            width: 100%;
            object-fit: cover;
            border-radius: $border-radius;
            transition: all 120ms ease-in-out;
        }

        &--fallback {
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: $panel-secondary-color;
        }

        > img.blog-post__fallback-logo {
            height: 60%;
            width: 60%;
            object-fit: contain;
        }
    }

    &__date {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    &__label {
        font-size: 12px;
        font-weight: 600;
        color: $badge-blog-color;
        background-color: $badge-blog-bg;
        border-radius: var(--border-radius, $border-radius);
        line-height: 1.4;
        height: min-content;
        padding: 2px 8px;
    }

    &__content {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    &__title {
        font-size: 18px;
        font-weight: 600;
        color: var(--on-background, $body-font-color);
    }

    &__subtitle {
        font-size: 14px;
        color: var(--on-background, $body-font-color);
    }

    &__created {
        font-size: 12px;
        font-weight: 700;
        color: var(--panel-color, $panel-font-color);
    }
}
</style>
